import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Server, Play, Square, Trash2, Plus, Search, Cpu, MemoryStick } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'
import StatusBadge from '../components/StatusBadge'
import CreateVMModal from '../components/CreateVMModal'
import { getVMs, startVM, stopVM, destroyVM } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import type { VM } from '../types'

export default function VMs() {
  const queryClient = useQueryClient()
  const { me } = useAuth()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'running' | 'stopped'>('all')
  const [showCreate, setShowCreate] = useState(false)

  const { data: vms = [], isLoading } = useQuery({
    queryKey: ['vms'],
    queryFn: getVMs,
    refetchInterval: 5000,
  })

  const startMutation = useMutation({
    mutationFn: startVM,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vms'] }),
  })

  const stopMutation = useMutation({
    mutationFn: stopVM,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vms'] }),
  })

  const destroyMutation = useMutation({
    mutationFn: destroyVM,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vms'] }),
  })

  const filteredVMs = vms.filter((vm: VM) => {
    const matchesSearch = vm.id.toLowerCase().includes(search.toLowerCase())
    const matchesFilter =
      filter === 'all' ||
      (filter === 'running' && vm.state === 'running') ||
      (filter === 'stopped' && vm.state !== 'running')
    return matchesSearch && matchesFilter
  })

  const handleAction = async (action: 'start' | 'stop' | 'destroy', id: string) => {
    if (action === 'destroy' && !confirm(`Are you sure you want to destroy VM ${id}?`)) {
      return
    }

    switch (action) {
      case 'start':
        startMutation.mutate(id)
        break
      case 'stop':
        stopMutation.mutate(id)
        break
      case 'destroy':
        destroyMutation.mutate(id)
        break
    }
  }

  const maxVMs = me?.plan?.limits?.max_vms
  const atCap = typeof maxVMs === 'number' && maxVMs >= 0 && vms.length >= maxVMs

  return (
    <div>
      {showCreate && <CreateVMModal onClose={() => setShowCreate(false)} />}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Virtual Machines</h1>
          <p className="mt-1 text-gray-500">
            {typeof maxVMs === 'number' && maxVMs >= 0
              ? `${vms.length} of ${maxVMs} VMs used${me?.plan?.name ? ` · ${me.plan.name} plan` : ''}`
              : 'Manage your VMs'}
          </p>
        </div>
        <Button
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setShowCreate(true)}
          disabled={atCap}
          title={atCap ? 'You have reached your plan VM limit' : 'Create a new VM'}
        >
          Create VM
        </Button>
      </div>

      {atCap && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          You have reached your plan limit of {maxVMs} VMs. Destroy a VM to create another.
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search VMs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-armored-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-2">
            {(['all', 'running', 'stopped'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === f
                    ? 'bg-armored-100 text-armored-700'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* VM List */}
      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-armored-600" />
          </div>
        </Card>
      ) : filteredVMs.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Server className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No VMs found</h3>
            <p className="mt-2 text-gray-500">
              {search || filter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create a VM to get started'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredVMs.map((vm: VM) => (
            <Card key={vm.id} padding="none">
              <div className="flex items-center justify-between p-4">
                <Link
                  to={`/vms/${encodeURIComponent(vm.id)}`}
                  className="flex items-center space-x-4 flex-1 min-w-0"
                >
                  <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
                    <Server className="h-6 w-6 text-armored-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{vm.id}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <MemoryStick className="h-3.5 w-3.5 mr-1" />
                        {vm.memory}
                      </span>
                      <span className="flex items-center">
                        <Cpu className="h-3.5 w-3.5 mr-1" />
                        {vm.kernel_profile}
                      </span>
                      {vm.ip && <span>IP: {vm.ip}</span>}
                    </div>
                  </div>
                </Link>

                <div className="flex items-center space-x-3">
                  <StatusBadge status={vm.state} />

                  <div className="flex items-center space-x-1">
                    {vm.state !== 'running' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAction('start', vm.id)}
                        loading={startMutation.isPending}
                        title="Start"
                      >
                        <Play className="h-4 w-4 text-green-600" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAction('stop', vm.id)}
                        loading={stopMutation.isPending}
                        title="Stop"
                      >
                        <Square className="h-4 w-4 text-gray-600" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAction('destroy', vm.id)}
                      loading={destroyMutation.isPending}
                      title="Destroy"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
